/**
 * Strichsymbole für Navigation und Kopfzeile.
 *
 * Vorher standen hier Unicode-Zeichen: ⌂ ✓ ◷ ⌗ ✉. Die kosten nichts, haben
 * aber zwei Nachteile, die draußen zählen. Erstens zeichnet jedes System sie
 * anders – auf Android steht an derselben Stelle ein anderes Bild als auf dem
 * iPhone, teils als farbiges Emoji. Zweitens gibt es für die meisten Begriffe
 * schlicht kein passendes Zeichen; ⌗ für „Kunden" und ◷ für „Fällig" muss man
 * raten.
 *
 * Deshalb eigene SVG: gleiche Strichstärke, gleiches Raster, gleiche Farbe
 * wie der Text daneben (currentColor). Sie erben damit auch den orangen
 * Zustand der aktiven Navigation, ohne dass es dafür eine zweite Regel
 * braucht.
 *
 * Die Strichstärke ist 2 und nicht 1,5: bei Sonne auf dem Platz verschwindet
 * ein feiner Strich, und die Leiste unten wird mit dem Daumen bedient, nicht
 * mit der Maus betrachtet.
 */

import type { SVGProps } from 'react'

type Eigenschaften = SVGProps<SVGSVGElement> & { groesse?: number }

function Rahmen({ groesse = 24, children, ...rest }: Eigenschaften) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Die Symbole stehen immer neben ihrer Beschriftung. Ein zweites Mal
      // vorgelesen zu werden hilft niemandem.
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

/** Übersicht: Haus. */
export function SymbolHaus(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
      <path d="M9.5 20v-5.5h5V20" />
    </Rahmen>
  )
}

/** Prüfungen: Klemmbrett mit Haken – das Werkzeug, nicht das Ergebnis. */
export function SymbolPruefung(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M9 4.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6.5a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1" />
      <path d="m8.75 13.5 2.25 2.25 4.25-4.5" />
    </Rahmen>
  )
}

/** Fällig: Uhr. */
export function SymbolUhr(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Rahmen>
  )
}

/**
 * Kunden: Gebäude.
 *
 * Nicht die übliche Personengruppe: Kunden sind hier Gemeinden, Kitas und
 * Schulen, also Einrichtungen. Ein Haus mit Fenstern trifft das, zwei Köpfe
 * würden in die Irre führen.
 */
export function SymbolKunden(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M4 21V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V21" />
      <path d="M14 10h4.5A1.5 1.5 0 0 1 20 11.5V21" />
      <path d="M2.5 21h19" />
      <path d="M7 8h1.5M11 8H12M7 12h1.5M11 12H12M7 16h1.5M11 16H12M17 14h.5M17 17.5h.5" />
    </Rahmen>
  )
}

/** Anfragen: Umschlag. */
export function SymbolBrief(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </Rahmen>
  )
}

/** Menü: drei Striche. */
export function SymbolMenue(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Rahmen>
  )
}

/** Schließen. */
export function SymbolSchliessen(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Rahmen>
  )
}

/** Suche: Lupe. */
export function SymbolLupe(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </Rahmen>
  )
}

/** Konto: Kopf und Schultern. Steht für die eigene Person, nicht für Kunden. */
export function SymbolPerson(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </Rahmen>
  )
}

/** Kataloge: gestapelte Karten. */
export function SymbolKatalog(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <rect x="3" y="4" width="18" height="5" rx="1.5" />
      <rect x="3" y="12.5" width="18" height="5" rx="1.5" />
      <path d="M6.5 20.5h11" />
    </Rahmen>
  )
}

/**
 * Prüfabläufe: Schrittfolge von oben nach unten.
 *
 * Die erste Fassung waren drei verbundene Kreise – das ist das übliche
 * Zeichen für „Teilen" und wurde auch so gelesen. Eine Zeitleiste mit
 * Punkten und Beschriftung trifft das Gemeinte: ein Schritt nach dem
 * anderen.
 */
export function SymbolAblauf(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <circle cx="6" cy="6" r="2.25" />
      <circle cx="6" cy="18" r="2.25" />
      <path d="M6 8.25v7.5" />
      <path d="M11 6h9M11 18h9" />
    </Rahmen>
  )
}

/** Warteschlange: Pfeil nach oben in eine Ablage. */
export function SymbolWarteschlange(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M12 16V4.5" />
      <path d="m7.5 9 4.5-4.5L16.5 9" />
      <path d="M4.5 15v3A1.5 1.5 0 0 0 6 19.5h12a1.5 1.5 0 0 0 1.5-1.5v-3" />
    </Rahmen>
  )
}

/**
 * Verwaltung: Schieberegler.
 *
 * Ein Zahnrad wäre das gewohnte Zeichen, lässt sich aber mit zwei Pixeln
 * Strichstärke nicht zeichnen: aus den acht Zähnen wird bei 20 Pixeln eine
 * Sonne. Drei Regler sind bei dieser Größe eindeutig und meinen dasselbe.
 */
export function SymbolZahnrad(p: Eigenschaften) {
  return (
    <Rahmen {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
      <circle cx="9" cy="7" r="2" fill="var(--color-white, #fff)" />
      <circle cx="15.5" cy="12" r="2" fill="var(--color-white, #fff)" />
      <circle cx="8" cy="17" r="2" fill="var(--color-white, #fff)" />
    </Rahmen>
  )
}
